import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { OverflowList } from 'react-responsive-overflow-list'
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material'
import { type Editor } from '@tiptap/react'

import { uploadFile } from '~api/services/utils/upload_utils'
import { Button, Icon, TextField } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { typography } from '~tokens/internal'
import { AssetTypeEnum } from '~types'
import { ensureUrlProtocol } from '~utils/navigationUtils'

import './RichTextEditorToolbar.scss'

interface TemplateLink {
  text: string
  href: string
}

interface RichTextEditorToolbarProps {
  editor: Editor
  isTemplateEditor: boolean
  templateLinks: TemplateLink[]
  iconSize?: string
}

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px']

const TEXT_COLORS = [
  '#000000',
  '#434343',
  '#666666',
  '#999999',
  '#b7b7b7',
  '#cccccc',
  '#d9d9d9',
  '#ffffff',
  '#980000',
  '#ff0000',
  '#ff9900',
  '#ffff00',
  '#00ff00',
  '#00ffff',
  '#4a86e8',
  '#0000ff',
  '#9900ff',
  '#ff00ff',
]

const BG_COLORS = [
  'transparent',
  '#fce5cd',
  '#fff2cc',
  '#d9ead3',
  '#d0e0e3',
  '#c9daf8',
  '#d9d2e9',
  '#ead1dc',
  '#f4cccc',
  '#ea9999',
  '#f9cb9c',
  '#ffe599',
  '#b6d7a8',
  '#a2c4c9',
  '#a4c2f4',
  '#b4a7d6',
  '#d5a6bd',
  '#e06666',
]

const RichTextEditorToolbar: React.FC<RichTextEditorToolbarProps> = ({
  editor,
  isTemplateEditor,
  templateLinks,
  iconSize,
}) => {
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null)
  const [bgColorAnchor, setBgColorAnchor] = useState<HTMLElement | null>(null)
  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null)
  const [linkUrl, setLinkUrl] = useState<string>('')
  const [fontSizeAnchor, setFontSizeAnchor] = useState<HTMLElement | null>(null)
  const [templateLinkAnchor, setTemplateLinkAnchor] = useState<HTMLElement | null>(null)
  const [overflowMenuAnchor, setOverflowMenuAnchor] = useState<HTMLElement | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const userId = useSelector((state: RootState) => state?.auth.userId)
  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )

  const iconFontSize = iconSize ?? typography.fontSize.xs
  const btnClass = 'rich-text-editor-toolbar__btn'
  const activeBtnClass = 'rich-text-editor-toolbar__btn rich-text-editor-toolbar__btn--active'
  const hasTemplateGroup = isTemplateEditor && templateLinks.length > 0

  // Re-render toolbar when selection or document changes
  const [, forceRerender] = useState(0)
  useEffect(() => {
    if (!editor) return
    const handler = (): void => forceRerender((v) => v + 1)
    editor.on('selectionUpdate', handler)
    editor.on('transaction', handler)

    return (): void => {
      editor.off('selectionUpdate', handler)
      editor.off('transaction', handler)
    }
  }, [editor])

  const handleInsertLink = (): void => {
    if (linkUrl.trim()) {
      editor
        .chain()
        .focus()
        .setLink({ href: ensureUrlProtocol(linkUrl), target: '_blank' })
        .run()
    } else {
      editor.chain().focus().unsetLink().run()
    }
    setLinkAnchor(null)
    setLinkUrl('')
  }

  const handleOpenLinkPopover = (event: React.MouseEvent<HTMLElement>): void => {
    setLinkUrl(editor.getAttributes('link').href || '')
    setLinkAnchor(event.currentTarget)
  }

  const insertTemplateLink = (templateLink: TemplateLink, onClose?: () => void): void => {
    editor
      .chain()
      .focus()
      .insertContent(`<a href="${templateLink.href}" target="_blank">${templateLink.text}</a>`)
      .run()
    onClose?.()
  }

  interface ToolbarButton {
    id: string
    title: string
    icon?: string
    text?: string
    onClick: (event: React.MouseEvent<HTMLElement>) => void
    isActive: boolean
  }

  const toolbarButtons: ToolbarButton[] = [
    {
      id: 'bold',
      title: 'Bold',
      icon: 'formatBold',
      onClick: () => editor.chain().focus().toggleBold().run(),
      isActive: editor.isActive('bold'),
    },
    {
      id: 'italic',
      title: 'Italic',
      icon: 'formatItalic',
      onClick: () => editor.chain().focus().toggleItalic().run(),
      isActive: editor.isActive('italic'),
    },
    {
      id: 'underline',
      title: 'Underline',
      icon: 'formatUnderline',
      onClick: () => editor.chain().focus().toggleUnderline().run(),
      isActive: editor.isActive('underline'),
    },
    {
      id: 'fontSize',
      title: 'Font Size',
      icon: 'formatSize',
      onClick: (e: React.MouseEvent<HTMLElement>) => setFontSizeAnchor(e.currentTarget),
      isActive: false,
    },
    {
      id: 'textColor',
      title: 'Text Color',
      icon: 'formatColorText',
      onClick: (e: React.MouseEvent<HTMLElement>) => setColorAnchor(e.currentTarget),
      isActive: ((): boolean => {
        const c = editor.getAttributes('textStyle')?.color
        return Boolean(c) && String(c).toLowerCase() !== '#000000'
      })(),
    },
    {
      id: 'highlight',
      title: 'Highlight Color',
      icon: 'formatBorderColor',
      onClick: (e: React.MouseEvent<HTMLElement>) => setBgColorAnchor(e.currentTarget),
      isActive: Boolean(editor.getAttributes('highlight')?.color) || editor.isActive('highlight'),
    },
    {
      id: 'clearFormatting',
      title: 'Clear Formatting',
      icon: 'formatClear',
      onClick: () => editor.chain().focus().clearNodes().unsetAllMarks().run(),
      isActive: false,
    },
    {
      id: 'alignLeft',
      title: 'Align Left',
      icon: 'formatAlignLeft',
      onClick: () => editor.chain().focus().setTextAlign('left').run(),
      isActive: false,
    },
    {
      id: 'alignCenter',
      title: 'Align Center',
      icon: 'formatAlignCenter',
      onClick: () => editor.chain().focus().setTextAlign('center').run(),
      isActive: editor.isActive({ textAlign: 'center' }),
    },
    {
      id: 'alignRight',
      title: 'Align Right',
      icon: 'formatAlignRight',
      onClick: () => editor.chain().focus().setTextAlign('right').run(),
      isActive: editor.isActive({ textAlign: 'right' }),
    },
    {
      id: 'alignJustify',
      title: 'Align Justify',
      icon: 'formatAlignJustify',
      onClick: () => editor.chain().focus().setTextAlign('justify').run(),
      isActive: editor.isActive({ textAlign: 'justify' }),
    },
    {
      id: 'increaseIndent',
      title: 'Increase Indent',
      icon: 'formatIndentIncrease',
      onClick: () => editor.chain().focus().sinkListItem('listItem').run(),
      isActive: false,
    },
    {
      id: 'decreaseIndent',
      title: 'Decrease Indent',
      icon: 'formatIndentDecrease',
      onClick: () => editor.chain().focus().liftListItem('listItem').run(),
      isActive: false,
    },
    {
      id: 'blockquote',
      title: 'Blockquote',
      icon: 'formatQuote',
      onClick: () => editor.chain().focus().toggleBlockquote().run(),
      isActive: editor.isActive('blockquote'),
    },
    {
      id: 'bulletList',
      title: 'Bullet List',
      icon: 'formatListBulleted',
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      isActive: editor.isActive('bulletList'),
    },
    {
      id: 'numberedList',
      title: 'Numbered List',
      icon: 'formatListNumbered',
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: editor.isActive('orderedList'),
    },
    {
      id: 'link',
      title: 'Insert Link',
      icon: 'link',
      onClick: handleOpenLinkPopover,
      isActive: editor.isActive('link'),
    },
    {
      id: 'image',
      title: 'Insert Image',
      icon: 'image',
      onClick: () => fileInputRef.current?.click(),
      isActive: false,
    },
    ...(hasTemplateGroup
      ? [
          {
            id: 'templateLink',
            title: 'Insert Template Link',
            text: 'TL',
            onClick: (e: React.MouseEvent<HTMLElement>) => setTemplateLinkAnchor(e.currentTarget),
            isActive: false,
          },
        ]
      : []),
  ]

  const GROUP_START_IDS = new Set([
    'textColor',
    'alignLeft',
    'increaseIndent',
    'bulletList',
    'link',
    'templateLink',
  ])

  const renderButton = (button: ToolbarButton, isGroupStart = false): React.ReactNode => (
    <Tooltip key={button.id} title={button.title}>
      <IconButton
        size='small'
        onClick={button.onClick}
        className={`${button.isActive ? activeBtnClass : btnClass} ${
          isGroupStart ? 'rich-text-editor-toolbar__btn--group-start' : ''
        }`}
      >
        {button.text ? (
          <Typography
            className='rich-text-editor-toolbar__button-text'
            sx={{ fontSize: iconSize ?? typography.fontSize.xxs }}
          >
            {button.text}
          </Typography>
        ) : (
          <Icon
            name={button.icon as React.ComponentProps<typeof Icon>['name']}
            size={iconFontSize}
          />
        )}
      </IconButton>
    </Tooltip>
  )

  const renderToolbarButton = (button: ToolbarButton, _index: number): React.ReactNode => (
    <Box
      key={button.id}
      className={`rich-text-editor-toolbar__item ${
        GROUP_START_IDS.has(button.id) ? 'rich-text-editor-toolbar__item--has-divider' : ''
      }`}
    >
      {GROUP_START_IDS.has(button.id) && (
        <Divider
          orientation='vertical'
          flexItem
          className='rich-text-editor-toolbar__inline-divider'
        />
      )}
      {renderButton(button)}
    </Box>
  )

  const renderOverflowButton = (button: ToolbarButton, _index: number): React.ReactNode =>
    renderButton(button, false)

  return (
    <Box className='rich-text-editor-toolbar'>
      <OverflowList
        items={toolbarButtons}
        className='rich-text-editor-toolbar__groups'
        renderItem={renderToolbarButton}
        renderOverflow={(overflowItems: ToolbarButton[]) => (
          <Box className='rich-text-editor-toolbar__overflow-wrapper'>
            <Divider orientation='vertical' flexItem />
            <Tooltip title='More formatting'>
              <IconButton
                size='small'
                onClick={(e) => setOverflowMenuAnchor(e.currentTarget)}
                className={overflowMenuAnchor ? activeBtnClass : btnClass}
              >
                <Icon name='moreVert' size={iconFontSize} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={overflowMenuAnchor}
              open={Boolean(overflowMenuAnchor)}
              onClose={() => setOverflowMenuAnchor(null)}
            >
              <Box className='rich-text-editor-toolbar__overflow-menu-group'>
                {overflowItems.map(renderOverflowButton)}
              </Box>
            </Menu>
          </Box>
        )}
        maxRows={1}
        flushImmediately={true}
      />
      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box className='rich-text-editor-toolbar__link-popover'>
          <TextField
            size='small'
            label='URL'
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleInsertLink()
            }}
            fullWidth
          />
          <Box className='rich-text-editor-toolbar__button-group'>
            <Button size='small' variant='contained' onClick={handleInsertLink}>
              {linkUrl.trim() ? 'Apply' : 'Remove'}
            </Button>
            <Button size='small' variant='outlined' onClick={() => setLinkAnchor(null)}>
              Cancel
            </Button>
          </Box>
          {isTemplateEditor && templateLinks.length > 0 && (
            <Box>
              <Typography
                variant='caption'
                sx={{
                  color: 'text.secondary',
                }}
              >
                Template Links
              </Typography>
              {templateLinks.map((tl) => (
                <Button
                  key={tl.href}
                  size='small'
                  variant='text'
                  onClick={() => {
                    insertTemplateLink(tl, () => {
                      setLinkAnchor(null)
                      setLinkUrl('')
                    })
                  }}
                  className='rich-text-editor-toolbar__template-link-button'
                >
                  {tl.text}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      </Popover>
      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0]
          if (file && userId && selectedOrganization) {
            try {
              const response = await uploadFile(
                userId,
                selectedOrganization.id,
                file,
                AssetTypeEnum.enum.Misc,
                null,
                null,
                false,
              )
              if (response?.url) {
                editor
                  .chain()
                  .focus()
                  .insertContentAt(editor.state.selection.to, {
                    type: 'image',
                    attrs: { src: response.url },
                  })
                  .run()
              }
            } catch (error) {
              console.error('Error uploading image:', error)
            }
          }
          e.target.value = ''
        }}
      />
      <Menu
        anchorEl={templateLinkAnchor}
        open={Boolean(templateLinkAnchor)}
        onClose={() => setTemplateLinkAnchor(null)}
      >
        {templateLinks.map((tl) => (
          <MenuItem
            key={tl.href}
            onClick={() => insertTemplateLink(tl, () => setTemplateLinkAnchor(null))}
          >
            {tl.text}
          </MenuItem>
        ))}
      </Menu>
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box className='rich-text-editor-toolbar__color-grid'>
          {TEXT_COLORS.map((color) => (
            <IconButton
              key={color}
              size='small'
              onClick={() => {
                editor.chain().focus().setColor(color).run()
                setColorAnchor(null)
              }}
              className='rich-text-editor-toolbar__color-swatch'
              style={{ backgroundColor: color }}
            />
          ))}
        </Box>
      </Popover>
      <Popover
        open={Boolean(bgColorAnchor)}
        anchorEl={bgColorAnchor}
        onClose={() => setBgColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box className='rich-text-editor-toolbar__color-grid'>
          {BG_COLORS.map((color) => (
            <IconButton
              key={color}
              size='small'
              onClick={() => {
                if (color === 'transparent') {
                  editor.chain().focus().unsetHighlight().run()
                } else {
                  editor.chain().focus().toggleHighlight({ color }).run()
                }
                setBgColorAnchor(null)
              }}
              className='rich-text-editor-toolbar__color-swatch'
              style={{ backgroundColor: color === 'transparent' ? '#ffffff' : color }}
            />
          ))}
        </Box>
      </Popover>
      <Menu
        anchorEl={fontSizeAnchor}
        open={Boolean(fontSizeAnchor)}
        onClose={() => setFontSizeAnchor(null)}
      >
        {FONT_SIZES.map((size) => (
          <MenuItem
            key={size}
            onClick={() => {
              editor.chain().focus().setFontSize(size).run()
              setFontSizeAnchor(null)
            }}
          >
            <Typography sx={{ fontSize: size }}>{size}</Typography>
          </MenuItem>
        ))}
      </Menu>
      <Box className='rich-text-editor-toolbar__actions'>
        <Tooltip title='Undo'>
          <IconButton
            size='small'
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className={btnClass}
          >
            <Icon name='undo' size={iconFontSize} />
          </IconButton>
        </Tooltip>
        <Tooltip title='Redo'>
          <IconButton
            size='small'
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className={btnClass}
          >
            <Icon name='redo' size={iconFontSize} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default RichTextEditorToolbar
